// src/components/ambulance/AmbulanceResultCard.jsx
import React from "react";
import Card from "@/components/ui/Card.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  isFavoriteFacility,
  setFavoriteFacility,
  subscribeFacilityFavorites,
} from "@/services/facilityDirectoryPreferences.js";

const ICON_TIME = "/icons/patient/ambulance_fill.svg";
const ICON_MAP = "/icons/patient/distance_filled.svg";
const ICON_STAR = "/icons/patient/star_filled.svg";
const ICON_FAVORITE_FILLED = "/icons/patient/favorite.svg";
const ICON_FAVORITE_OUTLINE = "/icons/patient/favorite_outline.svg";

export default function AmbulanceResultCard({ ambulance, onLocate }) {
  const { t } = useLanguage();
  const [isFavorite, setIsFavorite] = React.useState(() =>
    isFavoriteFacility("ambulance", ambulance?.id) || !!ambulance?.isFavorite
  );

  if (!ambulance) return null;

  const hasMapTarget =
    (Number.isFinite(Number(ambulance?.lat)) && Number.isFinite(Number(ambulance?.lng))) ||
    Boolean(String(ambulance?.address || "").trim() || String(ambulance?.name || "").trim());
  const canLocate = hasMapTarget && typeof onLocate === "function";

  React.useEffect(() => {
    const sync = () =>
      setIsFavorite(isFavoriteFacility("ambulance", ambulance?.id) || !!ambulance?.isFavorite);
    sync();
    return subscribeFacilityFavorites(sync);
  }, [ambulance?.id, ambulance?.isFavorite]);

  const handleToggleFavorite = React.useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      const next = setFavoriteFacility("ambulance", ambulance?.id, !isFavorite);
      setIsFavorite(next);
    },
    [ambulance?.id, isFavorite]
  );

  const handleOpenMap = React.useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      onLocate?.(ambulance);
    },
    [ambulance, onLocate]
  );

  return (
    <Card
      className="
        afrimed-rail afrimed-rail--comfortable
        px-3 py-2
      "
    >
      <div className="flex items-start px-2 pt-3 pb-2 gap-3">
        <div
          className="
            h-[80px] w-[80px]
            rounded-[8px]
            overflow-hidden
            bg-[rgba(24,50,119,0.06)]
            border-[3px] border-white
            shadow-[0_0_0_1px_#DFE7FA]
            shrink-0
          "
        >
          <img
            src={ambulance.image}
            alt={ambulance.name}
            className="h-full w-full object-cover"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-title-medium text-on-surface truncate">
                {ambulance.name}
              </div>
              <div className="text-body-medium text-on-surface-var truncate">
                {ambulance.address}
              </div>
            </div>

            <button
              type="button"
              aria-label={
                isFavorite
                  ? t(
                      "appointments.directory.ambulances.favoriteRemoveAria",
                      "Remove ambulance from favourites"
                    )
                  : t(
                      "appointments.directory.ambulances.favoriteAddAria",
                      "Add ambulance to favourites"
                    )
              }
              className="h-9 w-9 flex items-center justify-center"
              onClick={handleToggleFavorite}
            >
              <img
                src={isFavorite ? ICON_FAVORITE_FILLED : ICON_FAVORITE_OUTLINE}
                alt=""
                className="h-5 w-5"
              />
            </button>
          </div>

          <div className="mt-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-4 text-body-medium text-on-surface-var">
              <div className="flex items-center gap-1.5">
                <span>{ambulance.time || "—"}</span>
                <img
                  src={ICON_TIME}
                  alt=""
                  className="h-[18px] w-[18px]"
                  draggable="false"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span>{ambulance.rating || "—"}</span>
                <img
                  src={ICON_STAR}
                  alt=""
                  className="h-6 w-6 translate-y-[2px]"
                  draggable="false"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleOpenMap}
              aria-label={t("appointments.directory.common.mapAria", "Open location in maps")}
              disabled={!canLocate}
              className={`shrink-0 flex items-center gap-0 text-body-large ${
                canLocate ? "text-on-surface-var" : "text-on-surface-var opacity-60"
              }`}
            >
              <span>{t("appointments.directory.common.map", "Map")}</span>
              <img src={ICON_MAP} alt="" className="h-6 w-6" draggable="false" />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}
